import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../../../app/store";
import { CustomApexAxisChartSeries, LineChartData } from "../../../features/chart/chartTypes";
import { postJsonWithAuth } from "../../../utils/api";
import { messstelle } from "../../../utils/apiTypes";
import { errorHandler } from "../../../utils/errorHandling";
import { logError, trendsDataToGraphLine } from "../../../utils/utils";
import { graphLinePayload, graphTab, trends, trendsList} from "./trendsTypes";
import { openSnackbar } from "../../../features/snackbar/globalSnackbarSlice";

const lineColors: Array<string> = [
  "#004754",
  "#bebd00",
  "#229855",
  "#b90000",
  "#83297f",
  "#c66300",
];

const initialState: trends = {
  selectedTab: "trendsTable",
  selectedTabVorher: "trendsTable",
  trendsGraphTabs: [],
  selectedCell: false,
  isGraphLoading: false,
};

const trendsSlice = createSlice({
  name: "trends",
  initialState,
  reducers: {
    // Setters

    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
    setSelectedTabVorher: (state, action: PayloadAction<string>) => {
      state.selectedTabVorher = action.payload;
    },
    setSelectedCell: (state, action: PayloadAction<boolean>) => {
      state.selectedCell = action.payload;
    },
    setAddGraphTab: (state, action: PayloadAction<graphTab>) => {
      const tempTab = action.payload;
      tempTab.index = state.trendsGraphTabs.length
      tempTab.name = `Tab ${tempTab.index + 1} (${tempTab.groupedBy})` 
      state.trendsGraphTabs.push(tempTab)
    },
    setAddGraphLines: (state, action: PayloadAction<Array<graphLinePayload>>) => {
      if (action.payload.length == 0) return;

      let tabName = action.payload[0].tabName;
      if (tabName == undefined) {
        const tabIndex = state.trendsGraphTabs.length;
        const graphTab: graphTab = {
          name: `Tab ${tabIndex + 1} (${action.payload[0].groupedBy})`,
          index: tabIndex,
          graphLoading: false,
          dataSet: {},
          groupedBy: action.payload[0].groupedBy,
          isOld: false,
        }
        state.trendsGraphTabs.push(graphTab);
        tabName = graphTab.name;
      }

      let allApexAxisChartSeries: CustomApexAxisChartSeries = [];
      let distinctColors = 0;

      if (action.payload[0].group in state.trendsGraphTabs.find(x => x.name === tabName)!.dataSet ) {
        allApexAxisChartSeries = state.trendsGraphTabs.find(x => x.name === tabName)!.dataSet[action.payload[0].group].series;
        distinctColors = new Set(allApexAxisChartSeries.map(x => x.color)).size;
      }
      
      action.payload.forEach(line => {
        if (!allApexAxisChartSeries.map(x => x.name).includes(line.graphLineName)) {
          allApexAxisChartSeries.unshift({name: line.graphLineName, color: lineColors[distinctColors], data: [], lastExtremum: (line.lastExtremum / line.data.length * 100)});
          line.data.forEach((set: any) => allApexAxisChartSeries[0].data.push(set));
        }
      });
      
      const lineChartDataObject: LineChartData = {
        title: action.payload[0].graphTitle,
        ereignisse: [], 
        geflaggteMesswerte: [],
        isNanoseconds: false,
        series: allApexAxisChartSeries,
        showEreignisse: false,
        yAxisTitleText: "",
        singlePoints: [],
      }       
      
      if (state.trendsGraphTabs.find(x => x.name === tabName) != undefined) {
        state.trendsGraphTabs.find(x => x.name === tabName)!.dataSet[action.payload[0].group] = lineChartDataObject;
      } 
      
    },
    setRemoveGraphTab: (state, action: PayloadAction<graphTab>) => {
      state.trendsGraphTabs = state.trendsGraphTabs.filter(x => x.index != action.payload.index);
    },
    setIsOld: (state, action: PayloadAction<boolean>) => {
      state.trendsGraphTabs.forEach((el) => el.isOld = action.payload);
    },
    setIsGraphLoading: (state, action: PayloadAction<boolean>) => {
      state.isGraphLoading = action.payload;
    }
  },
});

// Selectors
export const selectSelectedTab = (state: RootState): string =>
  state.trends.selectedTab;
export const selectSelectedTabVorher = (state: RootState): string =>
  state.trends.selectedTabVorher;
export const selectSelectedCell = (state: RootState): boolean =>
  state.trends.selectedCell;
export const selectTrendsGraphTabs = (state: RootState): graphTab[] =>
state.trends.trendsGraphTabs;
export const selectIsGraphLoading = (state: RootState): boolean => state.trends.isGraphLoading;


// Actions
export const { setSelectedTab, setSelectedTabVorher, setSelectedCell, setAddGraphTab, setRemoveGraphTab, setAddGraphLines, setIsOld, setIsGraphLoading } = trendsSlice.actions;

// Getter
export const getTrendsGraphLine = (idParam: number,
  messstelleParam: messstelle, 
  kenngroessenkurzname: string,
  groupedByParam: string,
  selectedTab?: string,
  ) => (dispatch: AppDispatch, getState: () => RootState): void => {

    const kenngroessen = getState().kenngroesseFilter.kenngroesseWertTemp;

    const body = {
      anfrageId: idParam,
      kenngroessenkurzname: kenngroessenkurzname,
      messstelle: messstelleParam.name,            
    }
    
    dispatch(setIsGraphLoading(true))

    postJsonWithAuth("TRENDSGRAPH", {}, body)
      .then((response: Array<trendsList>) => {
        const graphLines: Array<graphLinePayload> = [];
        response.forEach(trend => {
          const kenngroesse = kenngroessen.find(x => x.id == trend.id)!;

          const grouping = groupedByParam == "messstelle" ? messstelleParam.name : kenngroesse.kurzname
          const lineName = groupedByParam == "messstelle" ? `${kenngroesse.beschreibung} (${kenngroesse.kurzname} ${kenngroesse.feldname})` : `${messstelleParam.umspannwerk} ${messstelleParam.schaltfeld} (${messstelleParam.name} ${kenngroesse.feldname})`;
          const title = groupedByParam == "messstelle" ? `${messstelleParam.umspannwerk} ${messstelleParam.schaltfeld} (${messstelleParam.name})` : `${kenngroesse.beschreibung} (${kenngroesse.kurzname})`;
          graphLines.push({data: trendsDataToGraphLine(trend.datapoints), lastExtremum: trend.lastExtremum, tabName: selectedTab, groupedBy: groupedByParam, group: grouping, graphLineName: lineName, graphTitle: title});
        });
        dispatch(setAddGraphLines(graphLines));
      })
      .catch((error) => {
          logError(error);
          errorHandler(error, dispatch);
      }).finally(() => dispatch(setIsGraphLoading(false)));

};
    
    export const calculateTrend = (name: string, start: Date, ende: Date, messstellen: Array<string>, kenngroessen: Array<string>) => (dispatch: AppDispatch): void => {
      const body = {
        name: name,
        start: start,
        end: ende,
        messstellen: messstellen,
        kenngroessen: kenngroessen,
      }
      //dispatch(setLoadingKorrelation(true));
      postJsonWithAuth("TRENDSCALCULATE", {}, body)
        .then(() => {
          dispatch(openSnackbar({
              message: `Trend wurde zur Berechnung angefragt.`,
              severity: "success",
              duration: 6000,
            }));
        })
        .catch((error) => {
          logError(error);
          errorHandler(error, dispatch);
        });//.finally(() => dispatch(setLoadingKorrelation(false)));
    }

export default trendsSlice.reducer;
